import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API, api } from "@/lib/api";
import { Kicker } from "@/components/kit";
import { Sparkles, X, Send, Trash2, Lock, Loader2 } from "lucide-react";

const Bubble = ({ role, content }) => {
  const mine = role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className="max-w-[86%] rounded-[10px] px-3.5 py-2.5 text-[14px] leading-[1.65] whitespace-pre-wrap"
        style={
          mine
            ? { background: "var(--brass-bright)", color: "#fff" }
            : { background: "var(--paper-deep)", color: "var(--ink-soft)", border: "1px solid var(--hairline)" }
        }
      >
        {content}
      </div>
    </div>
  );
};

export const Assistant = ({ open, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [starters, setStarters] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [clearing, setClearing] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/assistant/history").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/assistant/starters").then((r) => setStarters(r.data.starters)).catch(() => {});
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, busy]);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || busy || clearing) return;
    setQ("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/assistant/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("table_token")}`,
        },
        body: JSON.stringify({ question: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          const evt = JSON.parse(line);
          if (evt.delta) {
            setMessages((m) => {
              const last = m[m.length - 1];
              // a concurrent Clear may have emptied the thread mid-stream
              if (!last || last.role !== "assistant") return m;
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: last.content + evt.delta };
              return copy;
            });
          }
          if (evt.error) {
            setMessages((m) => {
              const last = m[m.length - 1];
              if (!last || last.role !== "assistant") return m;
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: evt.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const last = m[m.length - 1];
        if (!last || last.role !== "assistant") return m;
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The assistant is unavailable right now." };
        return copy;
      });
    }
    setBusy(false);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[150] flex justify-end" data-testid="assistant-panel">
      <div className="absolute inset-0" style={{ background: "rgba(15,23,42,.35)" }} onClick={onClose} />
      <aside
        className="relative w-full sm:w-[440px] h-full flex flex-col fade-in"
        style={{ background: "var(--card)", borderLeft: "1px solid var(--hairline)" }}
      >
        <header
          className="flex items-center gap-3 px-5 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--hairline)" }}
        >
          <span
            className="flex items-center justify-center rounded-md shrink-0"
            style={{ width: 32, height: 32, background: "var(--accent-wash)", color: "var(--brass)" }}
          >
            <Sparkles size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold" style={{ color: "var(--ink)" }}>
              Table Assistant
            </div>
            <div className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--ink-mute)" }}>
              <Lock size={9} /> Grounded on this forum's record only
            </div>
          </div>
          <button
            className="btn-quiet !min-h-0 !px-2 !py-1.5"
            data-testid="assistant-clear"
            title="Clear conversation"
            disabled={busy || clearing}
            onClick={async () => {
              setClearing(true);
              try {
                await api.delete("/assistant/history");
                setMessages([]);
              } finally {
                setClearing(false);
              }
            }}
          >
            <Trash2 size={13} />
          </button>
          <button className="btn-quiet !min-h-0 !px-2 !py-1.5" data-testid="assistant-close" onClick={onClose}>
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
          {messages.length === 0 && (
            <div data-testid="assistant-empty">
              <p className="text-[15px] font-semibold" style={{ color: "var(--ink)" }}>
                Ask anything about The Table.
              </p>
              <p className="lede mt-1.5">
                Chapters, goals, commitments, fines, members, highlights and parked topics.
              </p>
              <div className="mt-5 space-y-2">
                <Kicker>Try</Kicker>
                {starters.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`assistant-starter-${i}`}
                    onClick={() => ask(s)}
                    className="block w-full text-left text-[13.5px] px-3.5 py-2.5 rounded-lg"
                    style={{ border: "1px solid var(--hairline)", color: "var(--ink-soft)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} />
          ))}
          {busy && messages[messages.length - 1]?.content === "" && (
            <div className="flex items-center gap-2 text-[13px]" style={{ color: "var(--ink-mute)" }}>
              <Loader2 size={13} className="animate-spin" /> Reading the record…
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          className="shrink-0 p-4 flex gap-2"
          style={{ borderTop: "1px solid var(--hairline)" }}
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
        >
          <input
            className="field"
            data-testid="assistant-input"
            placeholder="Ask about the forum…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="btn-ink !px-3.5" data-testid="assistant-send" disabled={busy || clearing || !q.trim()}>
            <Send size={14} />
          </button>
        </form>
      </aside>
    </div>,
    document.body,
  );
};

export default Assistant;
